import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate, useParams } from "react-router"
import Select, { createFilter } from "react-select"
import DatePicker from "react-datepicker"

import { httpOk, GET, POST, PUT } from "helpers/api"
import { formatDate } from "helpers/helpers"

function defaultItem() {
	return {
		name: "",
		comment: "",
		start_at: `${formatDate(new Date(), "yyyy-MM-dd")} 00:00:00`,
		end_at: "",
		motivation_calculation_type_id: 1,
		participant_mode: "by_position",
		is_active: true,
		is_show_in_cashbox: false,
		preparation_hall_type: null,
		restrictions_all_menus: false,
		use_payment_type_filter: false,
		use_order_type_sub_filter: false,
		use_counterparty_filter: false,
		depends_on_program_fulfillment: false,
		/** id торговых точек для типа расчёта 1; бэк: pos_ids */
		pos_ids: [],
		positions: [],
		users: [],
		restrict_menu_ids: [],
		restrict_category_ids: [],
		restrict_hall_ids: [],
		use_hall_filter: false,
		restrict_product_ids: [],
		restrict_payment_type_ids: [],
		restrict_order_type_ids: [],
		restrict_organization_ids: [],
		restriction_scope: null,
		restriction_mode: 'include',
		compensation: {
			calculation_type_id: 2,
			distribution_type_id: null,
			compensation_mode: "fixed",
			fixed_value: "",
			revenue_accrue_once: false,
			revenue_timeframe: null,
			revenue_date_from: null,
			revenue_date_to: null,
			revenue_month: null,
			quantity_accrue_once: false,
			is_per_unit: false,
		},
		revenue_tiers: [],
		quantity_tiers: [],
	}
}

/** Как в ответе GET /helper/employee-positions — только id и name */
function normalizeEmployeePosition(p) {
	if (!p || typeof p !== "object" || p.id == null) return null
	return {
		id: Number(p.id),
		name: p.name != null ? String(p.name) : "",
	}
}

function normalizePositionsFromApi(raw) {
	if (!Array.isArray(raw) || raw.length === 0) return []
	const first = raw[0]
	if (typeof first === "object" && first !== null && first.id != null) {
		return raw.map(normalizeEmployeePosition).filter(Boolean)
	}
	return raw.map((x) => Number(typeof x === "object" && x?.id != null ? x.id : x))
}

/** Как в ответе GET /helper/users — только id и name */
function normalizeParticipantUser(u) {
	if (!u || typeof u !== "object" || u.id == null) return null
	return {
		id: Number(u.id),
		name: u.name != null ? String(u.name) : "",
	}
}

function normalizeUsersFromApi(raw) {
	if (!Array.isArray(raw) || raw.length === 0) return []
	const first = raw[0]
	if (typeof first === "object" && first !== null && first.id != null) {
		return raw.map(normalizeParticipantUser).filter(Boolean)
	}
	return raw.map((x) => Number(typeof x === "object" && x?.id != null ? x.id : x))
}

function flattenCategoriesForSelect(tree) {
	const out = []
	const seen = new Set()
	function walk(items) {
		if (!Array.isArray(items)) return
		for (const c of items) {
			const id = c?.id
			if (id != null && !seen.has(id)) {
				seen.add(id)
				out.push({ id: Number(id), name: String(c.name || "") })
			}
			if (c.options?.length) walk(c.options)
		}
	}
	walk(Array.isArray(tree) ? tree : [])
	return out
}

function isMenuRestrictionScope(scope) {
	return scope === "menu"
}

function isCategoryRestrictionScope(scope) {
	return scope === "category"
}

function restrictionModeSelectOptions(t) {
	return [
		{ value: 'include', label: t('apply_for') },
		{ value: 'exclude', label: t('do_not_apply_for') },
	]
}

// Иначе «44» ищет по id товара, а не по названию («Gosht 44»).
const nameOnlyFilterOption = createFilter({
	stringify: (option) => option.label || '',
})

function restrictionScopeSelectOptions(t) {
	return [
		{ value: "menu", label: t("for_menu_category") },
		{ value: "category", label: t("by_category") },
		{ value: "product", label: t("for_position") },
	]
}

function normalizeAllProductsList(raw) {
	let arr = raw
	if (!Array.isArray(arr) && raw && typeof raw === "object") {
		arr = raw.data ?? raw.items ?? raw.products
	}
	if (!Array.isArray(arr)) return []
	return arr
		.map((p) => ({
			id: Number(p.product_id ?? p.id),
			name: String(p.name ?? ""),
		}))
		.filter((x) => !Number.isNaN(x.id) && x.id != null)
}

function formatNumberInputValue(value) {
	if (value === "" || value === null || value === undefined) return ""
	const stringValue = String(value).replace(",", ".")
	return stringValue.replace(/(\.\d*?)0+$/, "$1").replace(/\.$/, "")
}

/** Бэк принимает только массивы чисел (normalizeIntIdArray / uniqueIntIds). */
function mapParticipantsToIdArray(items) {
	const list = Array.isArray(items) ? items : []
	return list
		.map((x) =>
			typeof x === "object" && x !== null && x.id != null ? Number(x.id) : Number(x)
		)
		.filter((n) => !Number.isNaN(n) && n > 0)
}

function toPositiveIntArray(raw) {
	const list = Array.isArray(raw) ? raw : []
	return list.map(Number).filter((n) => !Number.isNaN(n) && n > 0)
}

function CreateUpdate() {
	const { t } = useTranslation()
	const { id } = useParams()
	const navigate = useNavigate()

	const [data, setData] = useState(defaultItem())
	const [poses, setPoses] = useState([])
	const [usersCatalog, setUsersCatalog] = useState([])
	const [usersPostions, setUsersPostions] = useState([])
	const [mainActiveTab, setMainActiveTab] = useState(1)
	const [motivationCalculationTypes, setMotivationCalculationTypes] = useState([])
	const [distributionTypes, setDistributionTypes] = useState([])
	const [participantPositionSearch, setParticipantPositionSearch] = useState("")
	const [participantUserSearch, setParticipantUserSearch] = useState("")
	const [menuFlat, setMenuFlat] = useState([])
	const [categoriesFlat, setCategoriesFlat] = useState([])
	const [allProductsList, setAllProductsList] = useState([])
	const [paymentTypes, setPaymentTypes] = useState([])
	const [orderTypeSubs, setOrderTypeSubs] = useState([])
	const [organizations, setOrganizations] = useState([])
	const [halls, setHalls] = useState([])

	function positionEntryId(entry) {
		if (entry && typeof entry === "object" && entry.id != null) return Number(entry.id)
		return Number(entry)
	}

	function positionIdSelected(positionId) {
		const id = Number(positionId)
		return (data.positions || []).some((x) => positionEntryId(x) === id)
	}

	function userEntryId(entry) {
		if (entry && typeof entry === "object" && entry.id != null) return Number(entry.id)
		return Number(entry)
	}

	function userIdSelected(userId) {
		const uid = Number(userId)
		return (data.users || []).some((x) => userEntryId(x) === uid)
	}

	function togglePosition(position) {
		const id = Number(position.id)
		let list = Array.isArray(data.positions) ? [...data.positions] : []
		const i = list.findIndex((x) => positionEntryId(x) === id)
		if (i >= 0) list.splice(i, 1)
		else {
			const slim = normalizeEmployeePosition(position)
			if (slim) list.push(slim)
		}
		setData({ ...data, positions: list })
	}

	function onRestrictionModeChange(mode) {
		setData({
			...data,
			restriction_mode: mode || 'include',
		})
	}

	function onRestrictionScopeChange(scope) {
		setData({
			...data,
			restriction_scope: scope || null,
			restrictions_all_menus: false,
			restrict_menu_ids: [],
			restrict_category_ids: [],
			restrict_product_ids: [],
		})
	}

	function toggleRestrictPaymentTypeId(typeId) {
		const id = Number(typeId)
		const ids = Array.isArray(data.restrict_payment_type_ids)
			? [...data.restrict_payment_type_ids].map(Number)
			: []
		const i = ids.indexOf(id)
		if (i >= 0) ids.splice(i, 1)
		else ids.push(id)
		setData({ ...data, restrict_payment_type_ids: ids })
	}

	function paymentTypeIdSelected(typeId) {
		const id = Number(typeId)
		return (data.restrict_payment_type_ids || []).some((x) => Number(x) === id)
	}

	function toggleRestrictOrderTypeId(orderTypeId) {
		const id = Number(orderTypeId)
		const ids = Array.isArray(data.restrict_order_type_ids)
			? [...data.restrict_order_type_ids].map(Number)
			: []
		const i = ids.indexOf(id)
		if (i >= 0) ids.splice(i, 1)
		else ids.push(id)
		const filtered = ids.filter((x) => !Number.isNaN(x) && x > 0)
		setData({
			...data,
			restrict_order_type_ids: filtered,
			use_order_type_sub_filter: filtered.length > 0,
		})
	}

	function orderTypeIdSelected(orderTypeId) {
		const id = Number(orderTypeId)
		return (data.restrict_order_type_ids || []).some((x) => Number(x) === id)
	}

	function toggleUser(user) {
		const id = Number(user.id)
		let list = Array.isArray(data.users) ? [...data.users] : []
		const i = list.findIndex((x) => userEntryId(x) === id)
		if (i >= 0) list.splice(i, 1)
		else {
			const slim = normalizeParticipantUser(user)
			if (slim) list.push(slim)
		}
		setData({ ...data, users: list })
	}

	const filteredUsersPostions = usersPostions.filter((p) =>
		String(p?.name ?? "").toLowerCase().includes(participantPositionSearch.toLowerCase())
	)
	const filteredUsersForParticipants = usersCatalog.filter((u) =>
		String(u?.name ?? "").toLowerCase().includes(participantUserSearch.toLowerCase())
	)

	const scopeOptions = useMemo(
		() => restrictionScopeSelectOptions(t),
		[t]
	)
	const restrictionModeOptions = useMemo(
		() => restrictionModeSelectOptions(t),
		[t]
	)
	const selectedCalculationType = motivationCalculationTypes.find(
		(option) => Number(option.id) === Number(data.motivation_calculation_type_id)
	)
	const selectedCalculationTypeKey = selectedCalculationType?.value ?? ""
	const getDistributionTypeIdByValue = (targetValue) => {
		const row = distributionTypes.find((item) => item.value === targetValue)
		if (!row || row.id == null) return null
		const id = Number(row.id)
		return Number.isNaN(id) ? null : id
	}
	const individualDistributionTypeId = getDistributionTypeIdByValue("individual")
	const equalSplitDistributionTypeId = getDistributionTypeIdByValue("equal_split")
	const isSharedPoolType = [
		"preparation_places",
		"general_preparation",
		"general_incoming",
	].includes(selectedCalculationTypeKey)
	const forceDistributionTypeId = isSharedPoolType ? null : individualDistributionTypeId
	// У личных продаж «Сумма» бывает за период целиком или за каждую проданную позицию;
	// у остальных типов сумма и так считается за штуку (приготовления, приходы, заказы)
	const isPersonalSales = selectedCalculationTypeKey === "personal_sales"
	const isPerUnit = isPersonalSales
		&& Number(data.compensation?.calculation_type_id) === 1
		&& !!data.compensation?.is_per_unit

	async function createUpdate(e) {
		e.preventDefault()

		const restrictOrderTypeIds = toPositiveIntArray(data.restrict_order_type_ids)
		const scope = data.restriction_scope

		const payload = {
			...data,
			start_at: data.start_at || formatDate(new Date(), 'yyyy-MM-dd'),
			end_at: data.end_at || null,
			motivation_calculation_type_id: Number(data.motivation_calculation_type_id) || 1,
			is_active: !!data.is_active,
			is_show_in_cashbox: !!data.is_show_in_cashbox,
			restrictions_all_menus: !!data.restrictions_all_menus,
			is_restrict_menus: !!data.restrictions_all_menus,
			use_payment_type_filter: !!data.use_payment_type_filter,
			use_order_type_sub_filter: restrictOrderTypeIds.length > 0,
			depends_on_program_fulfillment: !!data.depends_on_program_fulfillment,
			user_position_ids: mapParticipantsToIdArray(data.positions),
			user_ids: mapParticipantsToIdArray(data.users),
			restrict_menu_ids: scope === "menu" ? toPositiveIntArray(data.restrict_menu_ids) : [],
			restrict_category_ids: scope === "category" ? toPositiveIntArray(data.restrict_category_ids) : [],
			restrict_product_ids: scope === "product" ? toPositiveIntArray(data.restrict_product_ids) : [],
			restriction_mode: data.restriction_mode === 'exclude' ? 'exclude' : 'include',
			restrict_payment_type_ids: toPositiveIntArray(data.restrict_payment_type_ids),
			restrict_order_type_ids: restrictOrderTypeIds,
			restrict_organization_ids: data.use_counterparty_filter
				? toPositiveIntArray(data.restrict_organization_ids)
				: [],
			restrict_hall_ids: data.use_hall_filter
				? toPositiveIntArray(data.restrict_hall_ids)
				: [],
			pos_ids: toPositiveIntArray(data.pos_ids),
			compensation: {
				...data.compensation,
				calculation_type_id: Number(data.compensation.calculation_type_id) || 1,
				distribution_type_id: data.compensation.distribution_type_id
					? Number(data.compensation.distribution_type_id)
					: null,
				fixed_value:
					data.compensation.fixed_value === "" || data.compensation.fixed_value === null
						? null
						: Number(data.compensation.fixed_value),
				revenue_accrue_once: !!data.compensation.revenue_accrue_once,
				quantity_accrue_once: !!data.compensation.quantity_accrue_once,
				is_per_unit: isPerUnit,
			},
		}
		delete payload.positions
		delete payload.users
		delete payload.restriction_scope
		delete payload.poses
		delete payload.scope_pos_ids
		delete payload.employee_position_ids

		let response
		if (id) {
			response = await PUT(`/motivations/${id}`, payload, { loader: true })
		} else {
			response = await POST("/motivations", payload, { loader: true })
		}
		console.log(response)
		if (httpOk(response)) navigate("/motivations")
	}

	async function getData() {
		if (!id) return

		const response = await GET(`/motivations/${id}`, {}, { loader: true })
		if (httpOk(response)) {
			const r = response.data
			const positionsFromApi = Array.isArray(r?.positions)
				? r.positions
				: Array.isArray(r?.user_position_ids)
					? r.user_position_ids
					: Array.isArray(r?.employee_position_ids)
						? r.employee_position_ids
						: []
			const usersFromApi = Array.isArray(r?.users)
				? r.users
				: Array.isArray(r?.user_ids)
					? r.user_ids
					: []
			const merged = {
				...defaultItem(),
				...r,
				start_at: r?.start_at || "",
				end_at: r?.end_at || "",
				positions: normalizePositionsFromApi(positionsFromApi),
				users: normalizeUsersFromApi(usersFromApi),
			}
			merged.restrictions_all_menus =
				typeof r?.is_restrict_menus === "boolean"
					? r.is_restrict_menus
					: !!r?.restrictions_all_menus
			merged.is_show_in_cashbox = !!r?.is_show_in_cashbox
			merged.use_payment_type_filter = !!r?.use_payment_type_filter
			merged.depends_on_program_fulfillment = !!r?.depends_on_program_fulfillment
			merged.pos_ids = toPositiveIntArray(r.pos_ids)
			delete merged.scope_pos_ids
			merged.restrict_payment_type_ids = toPositiveIntArray(r.restrict_payment_type_ids)
			merged.restrict_order_type_ids = toPositiveIntArray(r.restrict_order_type_ids)
			merged.use_order_type_sub_filter =
				toPositiveIntArray(merged.restrict_order_type_ids).length > 0
			delete merged.user_ids
			delete merged.user_position_ids
			merged.restrict_category_ids = toPositiveIntArray(r.restrict_category_ids)
			merged.restrict_menu_ids = toPositiveIntArray(r.restrict_menu_ids)
			merged.restrict_organization_ids = toPositiveIntArray(r.restrict_organization_ids)
			merged.use_counterparty_filter = merged.restrict_organization_ids.length > 0
			merged.restrict_hall_ids = toPositiveIntArray(r.restrict_hall_ids)
			merged.use_hall_filter = merged.restrict_hall_ids.length > 0

			let restriction_scope =
				typeof merged.restriction_scope === "string" && merged.restriction_scope
					? merged.restriction_scope
					: null

			if (!restriction_scope) {
				if (merged.restrict_product_ids?.length) restriction_scope = "product"
				else if (merged.restrict_category_ids?.length) restriction_scope = "category"
				else if (merged.restrict_menu_ids?.length) restriction_scope = "menu"
			}
			if (
				restriction_scope &&
				restriction_scope !== "menu" &&
				restriction_scope !== "category" &&
				restriction_scope !== "product"
			) {
				restriction_scope = merged.restrict_product_ids?.length
					? "product"
					: merged.restrict_category_ids?.length
						? "category"
						: merged.restrict_menu_ids?.length
							? "menu"
							: null
			}
			merged.restriction_scope = restriction_scope
			merged.restriction_mode =
				merged.restriction_mode === 'exclude' ? 'exclude' : 'include'
			merged.restrictions_all_menus = false
			setData(merged)
		}
	}

	async function getPoses() {
		const response = await GET('/helper/poses', {}, { loader: true })
		if (httpOk(response)) setPoses(response.data)
	}

	async function getMotivationCalculationTypes() {
		const response = await GET(`/helper/motivations/calculation-types`, {}, { loader: true })
		if (httpOk(response)) setMotivationCalculationTypes(response.data)
	}
	async function getDistributionTypes() {
		const response = await GET(`/helper/motivations/distribution-types`, {}, { loader: true })
		if (httpOk(response)) setDistributionTypes(response.data)
	}

	async function getUsersPostions() {
		const response = await GET(`/helper/employee-positions`)
		if (httpOk(response)) setUsersPostions(response.data)
	}

	async function getUsers() {
		const response = await GET(`/helper/users`)
		if (httpOk(response)) setUsersCatalog(response.data)
	}

	async function getRestrictionMenu() {
		const response = await GET(`/helper/menu`, {}, { loader: true })
		if (httpOk(response)) {
			const raw = response.data
			const tree = Array.isArray(raw) ? raw : raw?.data
			setMenuFlat(flattenCategoriesForSelect(tree || []))
		}
	}

	async function getRestrictionCategories() {
		const response = await GET(`/helper/categories`, {}, { loader: true })
		if (httpOk(response)) {
			const raw = response.data
			const tree = Array.isArray(raw) ? raw : raw?.data
			setCategoriesFlat(flattenCategoriesForSelect(tree || []))
		}
	}

	async function getAllProductsList() {
		const response = await GET(`/helper/all-products`, {}, { loader: true })
		if (httpOk(response)) setAllProductsList(normalizeAllProductsList(response.data))
	}

	async function getPaymentTypes() {
		const response = await GET(`/helper/payment-types`, {}, { loader: true })
		if (httpOk(response)) setPaymentTypes(response.data)
	}

	async function getOrderTypeSubs() {
		const response = await GET(`/helper/order-type-subs`, {}, { loader: true })
		if (httpOk(response)) setOrderTypeSubs(response.data)
	}

	async function getOrganizations() {
		const response = await GET(`/helper/organizations`, {}, { loader: true })
		if (httpOk(response)) setOrganizations(response.data)
	}

	async function getHalls() {
		const response = await GET(`/helper/halls`, {}, { loader: true })
		if (httpOk(response)) setHalls(response.data)
	}

	useEffect(() => {
		getData()
		getPoses()
		getMotivationCalculationTypes()
		getDistributionTypes()
		getUsersPostions()
		getUsers()
		getRestrictionMenu()
		getRestrictionCategories()
		getAllProductsList()
		getPaymentTypes()
		getOrderTypeSubs()
		getOrganizations()
		getHalls()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (!usersPostions.length) return
		setData((prev) => {
			const pos = prev.positions
			if (!Array.isArray(pos) || pos.length === 0) return prev

			const slimList = (items) => items.map(normalizeEmployeePosition).filter(Boolean)

			if (typeof pos[0] === "object" && pos[0] !== null && pos[0].id != null) {
				const slimmed = slimList(pos)
				const alreadyShaped =
					slimmed.length === pos.length &&
					pos.every(
						(x, i) =>
							slimmed[i] &&
							Number(x.id) === slimmed[i].id &&
							String(x.name ?? "") === slimmed[i].name &&
							Object.keys(x).length === 2 &&
							"id" in x &&
							"name" in x
					)
				if (alreadyShaped) return prev
				return { ...prev, positions: slimmed }
			}

			const enriched = pos
				.map((pid) => usersPostions.find((u) => Number(u.id) === Number(pid)))
				.filter(Boolean)
			if (!enriched.length) return prev
			return { ...prev, positions: slimList(enriched) }
		})
	}, [usersPostions, data.positions])

	useEffect(() => {
		if (!usersCatalog.length) return
		setData((prev) => {
			const list = prev.users
			if (!Array.isArray(list) || list.length === 0) return prev

			const slimList = (items) => items.map(normalizeParticipantUser).filter(Boolean)

			if (typeof list[0] === "object" && list[0] !== null && list[0].id != null) {
				const slimmed = slimList(list)
				const alreadyShaped =
					slimmed.length === list.length &&
					list.every(
						(x, i) =>
							slimmed[i] &&
							Number(x.id) === slimmed[i].id &&
							String(x.name ?? "") === slimmed[i].name &&
							Object.keys(x).length === 2 &&
							"id" in x &&
							"name" in x
					)
				if (alreadyShaped) return prev
				return { ...prev, users: slimmed }
			}

			const enriched = list
				.map((uid) => usersCatalog.find((u) => Number(u.id) === Number(uid)))
				.filter(Boolean)
			if (!enriched.length) return prev
			return { ...prev, users: slimList(enriched) }
		})
	}, [usersCatalog, data.users])

	useEffect(() => {
		if (forceDistributionTypeId == null) return
		setData((prev) => {
			const current = Number(prev?.compensation?.distribution_type_id)
			if (current === forceDistributionTypeId) return prev
			return {
				...prev,
				compensation: {
					...prev.compensation,
					distribution_type_id: forceDistributionTypeId,
				},
			}
		})
	}, [forceDistributionTypeId])

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<div className="d-flex text-truncate">
					<h4 className="vertical-center">{t("motivation_programs")}</h4>
					<div className="vertical-center mt-1">
						<i className="uil uil-angle-double-right fz-20"></i>
					</div>
					<div className="vertical-center mt-1">
						<h6>{id ? t("edit") : t("create")}</h6>
					</div>
				</div>
			</div>

			<div className="card" style={{ 'minHeight': '500px' }}>
				<div className="card-body">
					<form onSubmit={createUpdate} autoComplete="off">
						<div className="tabs tabs-sm mb-2">
							<ul>
								<li className={mainActiveTab === 1 ? "active" : ""}
									onClick={() => setMainActiveTab(1)}>
									<span className="link">{t("motivation_program")}</span>
								</li>
								<li className={mainActiveTab === 2 ? "active" : ""}
									onClick={() => setMainActiveTab(2)}>
									<span className="link">{t("employees")}</span>
								</li>
								<li className={mainActiveTab === 3 ? "active" : ""}
									onClick={() => setMainActiveTab(3)}>
									<span className="link">{t("restrictions")}</span>
								</li>
								<li className={mainActiveTab === 4 ? "active" : ""}
									onClick={() => setMainActiveTab(4)}>
									<span className="link">{t("compensation")}</span>
								</li>
							</ul>
						</div>

						<div
							style={{
								width:
									mainActiveTab === 2 || mainActiveTab === 3 || mainActiveTab === 4
										? "100%"
										: "50%",
							}}>
							<div className={mainActiveTab === 1 ? "" : "d-none"}>
								<div className="form-group">
									<label>{t("title")}<span className="required-mark">*</span></label>
									<input
										type="text"
										className="form-control"
										value={data.name || ""}
										onChange={(e) =>
											setData({ ...data, name: e.target.value })
										}
									/>
								</div>
								<div className="form-group">
									<label>{t("comment")}</label>
									<textarea
										className="form-control"
										rows="3"
										value={data.comment || ""}
										onChange={(e) => setData({ ...data, comment: e.target.value })}
									/>
								</div>

								<div className="d-flex gap-4 mb-3">
									<div className="form-check">
										<input
											type="checkbox"
											className="form-check-input"
											id="motivation-is-active"
											checked={!!data.is_active}
											onChange={(e) => setData({ ...data, is_active: e.target.checked })}
										/>
										<label className="form-check-label" htmlFor="motivation-is-active">
											{t("active")}
										</label>
									</div>
									<div className="form-check">
										<input
											type="checkbox"
											className="form-check-input"
											id="motivation-is-show-in-cashbox"
											checked={!!data.is_show_in_cashbox}
											onChange={(e) => setData({ ...data, is_show_in_cashbox: e.target.checked })}
										/>
										<label
											className="form-check-label"
											htmlFor="motivation-is-show-in-cashbox">
											{t('show_in_cashbox')}
										</label>
									</div>
								</div>

								<div className="row">
									<div className="form-group col-md-6">
										<label>{t('launch_date')}<span className="required-mark">*</span></label>
										<div className="calendar-input">
											<DatePicker
												showMonthDropdown
												showYearDropdown
												dropdownMode="select"
												yearDropdownItemNumber={2}
												className="form-control"
												dateFormat="dd.MM.yyyy"
												selected={data.start_date ? new Date(data.start_date) : ''}
												onChange={(date) => setData({ ...data, 'start_date': formatDate(date, 'yyyy-MM-dd') })}
												maxDate={new Date()} />
											<i className="uil uil-calendar-alt"></i>
										</div>
									</div>
									<div className="form-group col-md-6">
										<label>{t('stop_date')}</label>
										<div className="calendar-input">
											<DatePicker
												showMonthDropdown
												showYearDropdown
												dropdownMode="select"
												yearDropdownItemNumber={2}
												minDate={new Date(new Date().getFullYear() - 1, 0, 1)}
												maxDate={new Date(new Date().getFullYear(), 11, 31)}
												className="form-control"
												dateFormat="dd.MM.yyyy"
												selected={data.end_date ? new Date(data.end_date) : ''}
												onChange={(date) => setData({ ...data, 'end_date': formatDate(date, 'yyyy-MM-dd') })} />
											<i className="uil uil-calendar-alt"></i>
										</div>
									</div>
								</div>

								<div className="form-group">
									<label>{t("calculation_type")}</label>
									<Select
										options={motivationCalculationTypes}
										value={motivationCalculationTypes.find(option => option.id === data.motivation_calculation_type_id)}
										onChange={(option) => setData({
											...data,
											'motivation_calculation_type_id': option ? option.id : 1,
										})}
										placeholder=""
										noOptionsMessage={() => t("list_empty")}
										getOptionLabel={(option) => option.name}
										getOptionValue={(option) => option.id}
									/>
								</div>
								{data.motivation_calculation_type_id === 1 &&
									<div className="form-group">
										<label>{t('poses')}<span className="required-mark">*</span></label>
										<Select
											isMulti
											isClearable
											options={poses}
											value={poses.filter((p) =>
												(data.pos_ids || []).some((id) => Number(id) === Number(p.id))
											)}
											onChange={(options) =>
												setData({
													...data,
													pos_ids: options ? options.map((o) => Number(o.id)) : [],
												})
											}
											placeholder=""
											noOptionsMessage={() => t('list_empty')}
											getOptionLabel={(option) => option.name}
											getOptionValue={(option) => option.id}
										/>
									</div>
								}
							</div>
							<div className={mainActiveTab === 2 ? "" : "d-none"}>
								<div className="form-group">
									<label>{t("program_participants")}:</label>
								</div>
								<div className="d-flex gap-2">
									<div className="form-check">
										<div className="radio-round"
											onClick={() => setData({ ...data, participant_mode: "by_position" })}
											style={{ 'backgroundColor': data.participant_mode === "by_position" ? "black" : "", }}
										/>
										<span onClick={() => setData({ ...data, participant_mode: "by_position" })}>
											{t("positions")}
										</span>
									</div>

									<div className="form-check">
										<div className="radio-round"
											onClick={() => setData({ ...data, participant_mode: "by_user" })}
											style={{ 'backgroundColor': data.participant_mode === "by_user" ? "black" : "" }}
										/>
										<span onClick={() => setData({ ...data, participant_mode: "by_user" })}>
											Сотрудники
										</span>
									</div>
								</div>
								{data.participant_mode === "by_position" && (
									<div className="row">
										<div className="col-md-6">
											<div className="form-group mb-2">
												<label className="small">{t("search")}:</label>
												<input
													type="text"
													className="form-control form-control-sm"
													value={participantPositionSearch}
													onChange={(e) => setParticipantPositionSearch(e.target.value)}
												/>
											</div>
											<div
												className="border rounded px-2 py-1"
												style={{ maxHeight: "280px", overflowY: "auto" }}>
												{filteredUsersPostions.map((p) => (
													<div key={p.id} className="form-check">
														<input
															className="form-check-input"
															type="checkbox"
															id={`motivation-emp-pos-${p.id}`}
															checked={positionIdSelected(p.id)}
															onChange={() => togglePosition(p)}
														/>
														<label
															className="form-check-label"
															htmlFor={`motivation-emp-pos-${p.id}`}>
															{p.name}
														</label>
													</div>
												))}
											</div>
										</div>
									</div>
								)}
								{data.participant_mode === "by_user" && (
									<div className="row">
										<div className="col-md-6">
											<div className="form-group mb-2">
												<label className="small">{t("search")}:</label>
												<input
													type="text"
													className="form-control form-control-sm"
													value={participantUserSearch}
													onChange={(e) => setParticipantUserSearch(e.target.value)}
												/>
											</div>
											<div
												className="border rounded px-2 py-1"
												style={{ maxHeight: "280px", overflowY: "auto" }}>
												{filteredUsersForParticipants.map((u) => (
													<div key={u.id} className="form-check">
														<input
															className="form-check-input"
															type="checkbox"
															id={`motivation-user-${u.id}`}
															checked={userIdSelected(u.id)}
															onChange={() => toggleUser(u)}
														/>
														<label
															className="form-check-label"
															htmlFor={`motivation-user-${u.id}`}>
															{u.name}
														</label>
													</div>
												))}
											</div>
										</div>
									</div>
								)}
							</div>
							<div className={mainActiveTab === 3 ? "" : "d-none"}>

								<div className="form-group border rounded p-3">
									<div className="row">
										<div className="col-md-6">
											<div className="form-group">
												<label className="mb-0 pt-2">{t("restriction_mode")}</label>
												<Select
													options={restrictionModeOptions}
													value={
														restrictionModeOptions.find(
															(option) => option.value === (data.restriction_mode || 'include')
														) ?? restrictionModeOptions[0]
													}
													onChange={(option) =>
														onRestrictionModeChange(option ? option.value : 'include')
													}
													placeholder=""
													noOptionsMessage={() => t("list_empty")}
													getOptionLabel={(option) => option.label}
													getOptionValue={(option) => option.value}
												/>
											</div>
										</div>
										<div className="col-md-6">
											<div className="form-group">
												<label className="mb-0 pt-2">{t('restriction_scope')}</label>
												<Select
													isClearable
													options={scopeOptions}
													value={
														scopeOptions.find(
															(option) => option.value === data.restriction_scope
														) ?? null
													}
													onChange={(option) =>
														onRestrictionScopeChange(option ? option.value : null)
													}
													placeholder=""
													noOptionsMessage={() => t("list_empty")}
													getOptionLabel={(option) => option.label}
													getOptionValue={(option) => option.value}
												/>
											</div>
										</div>
									</div>

									<label className="small text-muted d-block mb-2">
										{data.restriction_mode === 'exclude'
											? t('group_category_position_exclude_hint')
											: t('group_category_position')}
									</label>
									{isMenuRestrictionScope(data.restriction_scope) && (
										<Select
											isMulti
											isClearable
											options={menuFlat}
											value={menuFlat.filter((c) =>
												(data.restrict_menu_ids || []).some(
													(menuId) => Number(menuId) === Number(c.id)
												)
											)}
											onChange={(options) =>
												setData({
													...data,
													restrictions_all_menus: false,
													restrict_menu_ids:
														options && options.length
															? options.map((option) => option.id)
															: [],
													restrict_category_ids: [],
													restrict_product_ids: [],
												})
											}
											placeholder=""
											noOptionsMessage={() => t("list_empty")}
											filterOption={nameOnlyFilterOption}
											getOptionLabel={(option) => option.name}
											getOptionValue={(option) => option.id}
										/>
									)}
									{isCategoryRestrictionScope(data.restriction_scope) && (
										<Select
											isMulti
											isClearable
											options={categoriesFlat}
											value={categoriesFlat.filter((c) =>
												(data.restrict_category_ids || []).some(
													(catId) => Number(catId) === Number(c.id)
												)
											)}
											onChange={(options) =>
												setData({
													...data,
													restrictions_all_menus: false,
													restrict_menu_ids: [],
													restrict_category_ids:
														options && options.length
															? options.map((option) => option.id)
															: [],
													restrict_product_ids: [],
												})
											}
											placeholder=""
											noOptionsMessage={() => t("list_empty")}
											filterOption={nameOnlyFilterOption}
											getOptionLabel={(option) => option.name}
											getOptionValue={(option) => option.id}
										/>
									)}
									{data.restriction_scope === "product" && (
										<Select
											isMulti
											isClearable
											options={allProductsList}
											value={allProductsList.filter((p) =>
												(data.restrict_product_ids || []).some(
													(productId) => Number(productId) === Number(p.id)
												)
											)}
											onChange={(options) =>
												setData({
													...data,
													restrictions_all_menus: false,
													restrict_menu_ids: [],
													restrict_category_ids: [],
													restrict_product_ids:
														options && options.length
															? options.map((option) => option.id)
															: [],
												})
											}
											placeholder=""
											noOptionsMessage={() => t("list_empty")}
											filterOption={nameOnlyFilterOption}
											getOptionLabel={(option) => option.name}
											getOptionValue={(option) => option.id}
										/>
									)}
								</div>

								<fieldset className="border rounded p-3 mt-3">
									<legend className="float-none w-auto px-2 fs-6 mb-2">
										{t("payment_type_filter_fieldset")}
									</legend>
									<div className="form-check mb-3">
										<input
											type="checkbox"
											className="form-check-input"
											id="motivation-use-payment-filter"
											checked={!!data.use_payment_type_filter}
											onChange={(e) => {
												const v = e.target.checked
												setData({
													...data,
													use_payment_type_filter: v,
													restrict_payment_type_ids: v
														? data.restrict_payment_type_ids || []
														: [],
												})
											}}
										/>
										<label
											className="form-check-label"
											htmlFor="motivation-use-payment-filter">
											{t("use_payment_type_filter")}
										</label>
									</div>
									<div
										className={`row g-2 ${!data.use_payment_type_filter ? "text-muted" : ""}`}
										style={
											!data.use_payment_type_filter
												? { pointerEvents: "none", opacity: 0.65 }
												: undefined
										}>
										{paymentTypes.map((pt) => (
											<div key={pt.id} className="col-md-4">
												<div className="form-check mb-1">
													<input
														type="checkbox"
														className="form-check-input"
														id={`motivation-pt-${pt.id}`}
														disabled={!data.use_payment_type_filter}
														checked={paymentTypeIdSelected(pt.id)}
														onChange={() => toggleRestrictPaymentTypeId(pt.id)}
													/>
													<label
														className="form-check-label"
														htmlFor={`motivation-pt-${pt.id}`}>
														{pt.name}
													</label>
												</div>
											</div>
										))}
									</div>
								</fieldset>

								<fieldset className="border rounded p-3 mt-3">
									<legend className="float-none w-auto px-2 fs-6 mb-2">
										{t("order_type_filter_fieldset")}
									</legend>
									<div className="form-check mb-3">
										<input
											type="checkbox"
											className="form-check-input"
											id="motivation-use-order-type-sub-filter"
											checked={!!data.use_order_type_sub_filter}
											onChange={(e) => {
												const v = e.target.checked
												setData({
													...data,
													use_order_type_sub_filter: v,
													restrict_order_type_ids: v
														? data.restrict_order_type_ids || []
														: [],
												})
											}}
										/>
										<label
											className="form-check-label"
											htmlFor="motivation-use-order-type-sub-filter">
											{t("use_order_type_sub_filter")}
										</label>
									</div>
									<div
										className={`row g-2 ${!data.use_order_type_sub_filter ? "text-muted" : ""}`}
										style={
											!data.use_order_type_sub_filter
												? { pointerEvents: "none", opacity: 0.65 }
												: undefined
										}>
										{orderTypeSubs.map((sub) => (
											<div key={sub.id} className="col-md-4">
												<div className="form-check mb-1">
													<input
														type="checkbox"
														className="form-check-input"
														id={`motivation-ots-${sub.id}`}
														disabled={!data.use_order_type_sub_filter}
														checked={orderTypeIdSelected(sub.id)}
														onChange={() => toggleRestrictOrderTypeId(sub.id)}
													/>
													<label
														className="form-check-label"
														htmlFor={`motivation-ots-${sub.id}`}>
														{sub.name}
													</label>
												</div>
											</div>
										))}
									</div>
								</fieldset>

								<fieldset className="border rounded p-3 mt-3">
									<legend className="float-none w-auto px-2 fs-6 mb-2">
										{t("counterparty_filter_fieldset")}
									</legend>
									<div className="form-check mb-3">
										<input
											type="checkbox"
											className="form-check-input"
											id="motivation-use-counterparty-filter"
											checked={!!data.use_counterparty_filter}
											onChange={(e) => {
												const v = e.target.checked
												setData({
													...data,
													use_counterparty_filter: v,
													restrict_organization_ids: v
														? data.restrict_organization_ids || []
														: [],
												})
											}}
										/>
										<label
											className="form-check-label"
											htmlFor="motivation-use-counterparty-filter">
											{t("use_counterparty_filter")}
										</label>
									</div>
									{data.use_counterparty_filter && (
										<Select
											isMulti
											isClearable
											options={organizations}
											value={organizations.filter((o) =>
												(data.restrict_organization_ids || []).some(
													(orgId) => Number(orgId) === Number(o.id)
												)
											)}
											onChange={(options) =>
												setData({
													...data,
													restrict_organization_ids:
														options && options.length
															? options.map((option) => Number(option.id))
															: [],
												})
											}
											placeholder=""
											noOptionsMessage={() => t("list_empty")}
											filterOption={nameOnlyFilterOption}
											getOptionLabel={(option) => option.name}
											getOptionValue={(option) => option.id}
										/>
									)}
								</fieldset>

								<fieldset className="border rounded p-3 mt-3">
									<legend className="float-none w-auto px-2 fs-6 mb-2">
										{t("hall_filter_fieldset")}
									</legend>
									<div className="form-check mb-3">
										<input
											type="checkbox"
											className="form-check-input"
											id="motivation-use-hall-filter"
											checked={!!data.use_hall_filter}
											onChange={(e) => {
												const v = e.target.checked
												setData({
													...data,
													use_hall_filter: v,
													restrict_hall_ids: v ? data.restrict_hall_ids || [] : [],
												})
											}}
										/>
										<label
											className="form-check-label"
											htmlFor="motivation-use-hall-filter">
											{t("use_hall_filter")}
										</label>
									</div>
									{data.use_hall_filter && (
										<Select
											isMulti
											isClearable
											options={halls}
											value={halls.filter((h) =>
												(data.restrict_hall_ids || []).some(
													(hallId) => Number(hallId) === Number(h.id)
												)
											)}
											onChange={(options) =>
												setData({
													...data,
													restrict_hall_ids:
														options && options.length
															? options.map((option) => Number(option.id))
															: [],
												})
											}
											placeholder=""
											noOptionsMessage={() => t("list_empty")}
											filterOption={nameOnlyFilterOption}
											getOptionLabel={(option) => option.name}
											getOptionValue={(option) => option.id}
										/>
									)}
								</fieldset>
							</div>
							<div className={mainActiveTab === 4 ? "" : "d-none"}>
								<div className="border rounded p-3 mb-3">
									<div className="row align-items-center">
										<div className="col-md-6">
											<div className="d-flex flex-column gap-2">
												<div className="form-check mb-0">
													<div className="radio-round"
														onClick={() =>
															setData({
																...data,
																compensation: {
																	...data.compensation,
																	calculation_type_id: 1,
																	is_per_unit: false,
																},
															})
														}
														style={{
															backgroundColor:
																Number(data.compensation?.calculation_type_id) === 1 && !isPerUnit
																	? "black"
																	: "",
														}}
													/>
													<span
														onClick={() =>
															setData({
																...data,
																compensation: {
																	...data.compensation,
																	calculation_type_id: 1,
																	is_per_unit: false,
																},
															})
														}>
														{isPersonalSales ? t("amount_per_period") : t("amount")}
													</span>
												</div>
												{isPersonalSales &&
													<div className="form-check mb-0">
														<div className="radio-round"
															onClick={() =>
																setData({
																	...data,
																	compensation: {
																		...data.compensation,
																		calculation_type_id: 1,
																		is_per_unit: true,
																	},
																})
															}
															style={{
																backgroundColor: isPerUnit ? "black" : "",
															}}
														/>
														<span
															onClick={() =>
																setData({
																	...data,
																	compensation: {
																		...data.compensation,
																		calculation_type_id: 1,
																		is_per_unit: true,
																	},
																})
															}>
															{t("amount_per_unit")}
														</span>
													</div>
												}
												<div className="form-check mb-0">
													<div className="radio-round"
														onClick={() =>
															setData({
																...data,
																compensation: {
																	...data.compensation,
																	calculation_type_id: 2,
																	is_per_unit: false,
																},
															})
														}
														style={{
															backgroundColor:
																Number(data.compensation?.calculation_type_id) === 2
																	? "black"
																	: "",
														}}
													/>
													<span
														onClick={() =>
															setData({
																...data,
																compensation: {
																	...data.compensation,
																	calculation_type_id: 2,
																	is_per_unit: false,
																},
															})
														}>
														{t("percent_of_sum")}
													</span>
												</div>
											</div>
										</div>
										<div className="col-md-6">
											<div className="d-flex flex-column gap-2">
												<div className="form-check mb-0">
													<div className="radio-round"
														onClick={() =>
															setData({
																...data,
																compensation: {
																	...data.compensation,
																	distribution_type_id:
																		individualDistributionTypeId ?? 1,
																},
															})
														}
														style={{
															'backgroundColor':
																Number(data.compensation?.distribution_type_id) ===
																	(individualDistributionTypeId ?? 1)
																	? "black"
																	: "",
														}}
													/>
													<span
														onClick={() =>
															setData({
																...data,
																compensation: {
																	...data.compensation,
																	distribution_type_id:
																		individualDistributionTypeId ?? 1,
																},
															})
														}>
														{t("accrue_to_each")}
													</span>
												</div>
												<div className="form-check mb-0">
													<div className="radio-round"
														onClick={() => {
															if (!isSharedPoolType) return
															setData({
																...data,
																compensation: {
																	...data.compensation,
																	distribution_type_id:
																		equalSplitDistributionTypeId ?? 2,
																},
															})
														}}
														style={{
															'backgroundColor':
																Number(data.compensation?.distribution_type_id) ===
																	(equalSplitDistributionTypeId ?? 2)
																	? "black"
																	: "",
														}}
													/>
													<span
														style={{
															opacity: !isSharedPoolType ? 0.6 : 1,
															cursor: !isSharedPoolType ? "not-allowed" : "pointer",
														}}
														onClick={() => {
															if (!isSharedPoolType) return
															setData({
																...data,
																compensation: {
																	...data.compensation,
																	distribution_type_id:
																		equalSplitDistributionTypeId ?? 2,
																},
															})
														}}>
														{t("distribute_among_participants")}
													</span>
												</div>
											</div>
										</div>
									</div>
								</div>

								<div className="border rounded p-3">
									<div className="d-flex align-items-center gap-2">
										<div className="form-check mb-0 w-100">
											<div className="radio-round"
												onClick={() => setData({ ...data, compensation: { ...data.compensation, compensation_mode: "fixed" } })}
												style={{ 'backgroundColor': data.compensation?.compensation_mode === "fixed" ? "black" : "" }}
											/>
											<span onClick={() => setData({ ...data, compensation: { ...data.compensation, compensation_mode: "fixed" } })}>
												{t("fixed_compensation")}
											</span>
										</div>
										<input type="number" step="0.01" min="0" className="form-control"
											disabled={data.compensation?.compensation_mode !== "fixed"}
											value={
												data.compensation?.fixed_value === "" ||
													data.compensation?.fixed_value === null ||
													data.compensation?.fixed_value === undefined
													? ""
													: formatNumberInputValue(data.compensation.fixed_value)
											}
											onChange={(e) => setData({ ...data, compensation: { ...data.compensation, fixed_value: e.target.value } })}
										/>
									</div>
								</div>
							</div>
						</div>

						<div className="btn-wrapper gap-2 mt-5">
							<button type="button" className="btn btn-warning btn-rounded btn-wide"
								onClick={() => navigate(-1)}>
								{t("cancel")}
							</button>
							<button type="submit" className="btn btn-primary btn-rounded btn-wide">
								{t("save")}
							</button>
						</div>
					</form>
				</div>
			</div>
		</>
	)
}

export default CreateUpdate
